/**
 * Штатный трекер Matomo — очередь `window._paq` и загрузка matomo.js. Повторяет то, что делал
 * `@jonkoops/matomo-tracker` 0.7 (пакет брошен автором): порядок инструкций при старте, `setCustomUrl` и
 * `setDocumentTitle` перед каждым просмотром и событием.
 */

import type { IConfig } from '@/configuration/models/IConfig';
import type { AnalyticsEvent, PageViewEvent } from '../../types/analytics.types';

type Instruction = [string, ...unknown[]];

declare global {
    interface Window {
        _paq?: Instruction[];
    }
}

type MatomoConfig = NonNullable<NonNullable<IConfig['application']['analytics']>['matomo']>;

export type MatomoTrackerOptions = Omit<MatomoConfig, 'enabled'>;

export interface IMatomoTracker {
    pushInstruction(name: string, ...args: unknown[]): void;
    trackPageView(context?: Partial<PageViewEvent>): void;
    trackEvent(event: AnalyticsEvent): void;
}

const DEFAULT_HEARTBEAT_SECONDS = 15;

export const createMatomoTracker = (options: MatomoTrackerOptions): IMatomoTracker => {
    if (!options.urlBase) throw new Error('Matomo urlBase is required.');
    if (!options.siteId) throw new Error('Matomo siteId is required.');

    const pushInstruction = (name: string, ...args: unknown[]) => {
        window._paq?.push([name, ...args]);
    };

    const track = (data: Instruction, { documentTitle, href, customDimensions }: Partial<PageViewEvent> = {}) => {
        customDimensions?.forEach(({ id, value }) => pushInstruction('setCustomDimension', id, value));
        pushInstruction('setCustomUrl', href ?? window.location.href);
        pushInstruction('setDocumentTitle', documentTitle ?? window.document.title);
        pushInstruction(...data);
    };

    window._paq = window._paq ?? [];
    // Очередь уже заполнена — трекер подключён раньше (повторная инициализация модуля): второй matomo.js не нужен.
    if (window._paq.length === 0) {
        const urlBase = options.urlBase.endsWith('/') ? options.urlBase : `${options.urlBase}/`;
        pushInstruction('setTrackerUrl', options.trackerUrl ?? `${urlBase}matomo.php`);
        pushInstruction('setSiteId', options.siteId);
        if (options.userId) pushInstruction('setUserId', options.userId);
        Object.entries(options.configurations ?? {}).forEach(([name, args]) =>
            Array.isArray(args) ? pushInstruction(name, ...args) : pushInstruction(name, args),
        );
        if (options.heartBeat?.active ?? true) {
            pushInstruction('enableHeartBeatTimer', options.heartBeat?.seconds ?? DEFAULT_HEARTBEAT_SECONDS);
        }
        pushInstruction('enableLinkTracking', options.linkTracking ?? true);

        const script = document.createElement('script');
        script.async = true;
        script.defer = true;
        script.src = options.srcUrl || `${urlBase}matomo.js`;
        const firstScript = document.getElementsByTagName('script')[0];
        firstScript?.parentNode?.insertBefore(script, firstScript);
    }

    return {
        pushInstruction,
        trackPageView: (context) => track(['trackPageView'], context),
        trackEvent: ({ category, action, name, value, customDimensions }) => {
            if (!category || !action) throw new Error('Error: category and action are required.');
            track(['trackEvent', category, action, name, value], { customDimensions });
        },
    };
};
